import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Save, ArrowLeft, AlertCircle, CheckCircle2, RotateCcw, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardTitle } from '@/components/Card';
import { Button } from '@/components/Button';
import { Field, Input } from '@/components/Input';
import { Combobox } from '@/components/Combobox';
import { StatusPill } from '@/components/StatusPill';
import { Kbd } from '@/components/Kbd';
import { api } from '@/lib/api';
import { date } from '@/lib/format';
import { toast } from '@/components/Toaster';

type Status   = 'Open' | 'Working' | 'Completed' | 'Cancelled';
type Priority = 'Low' | 'Medium' | 'High' | 'Urgent';

interface Project { id: string; name: string; project_name: string; }
interface Task {
  id: string; name: string;
  subject: string;
  project_id?: string;
  parent_doctype?: string;
  parent_id?: string;
  assigned_to?: string;
  status: Status;
  priority: Priority;
  due_date?: string;
  description?: string;
}

const PARENT_DOCTYPES: { value: string; label: string }[] = [
  { value: 'customer',         label: 'Customer' },
  { value: 'supplier',         label: 'Supplier' },
  { value: 'lead',             label: 'Lead' },
  { value: 'contact',          label: 'Contact' },
  { value: 'opportunity',      label: 'Opportunity' },
  { value: 'asset',            label: 'Asset' },
  { value: 'purchase_order',   label: 'Purchase Order' },
  { value: 'sales_invoice',    label: 'Sales Invoice' },
  { value: 'purchase_invoice', label: 'Purchase Invoice' },
];

export function TaskForm() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: existing } = useQuery({
    queryKey: ['task', id],
    enabled: !isNew,
    queryFn: () => api<Task>(`/projects/tasks/${id}`),
  });
  const { data: projectsResp } = useQuery({
    queryKey: ['projects'],
    queryFn:  () => api<{ items: Project[] }>('/projects/projects'),
  });
  const projects = projectsResp?.items ?? [];

  const [subject, setSubject]         = useState('');
  const [projectID, setProjectID]     = useState('');
  const [parentDoctype, setParentDoctype] = useState('');
  const [parentID, setParentID]       = useState('');
  const [assignee, setAssignee]       = useState('');
  const [priority, setPriority]       = useState<Priority>('Medium');
  const [status, setStatus]           = useState<Status>('Open');
  const [dueDate, setDueDate]         = useState('');
  const [description, setDescription] = useState('');
  const [err, setErr]                 = useState<string | null>(null);

  useEffect(() => {
    if (!existing) return;
    setSubject(existing.subject);
    setProjectID(existing.project_id ?? '');
    setParentDoctype(existing.parent_doctype ?? '');
    setParentID(existing.parent_id ?? '');
    setAssignee(existing.assigned_to ?? '');
    setPriority(existing.priority ?? 'Medium');
    setStatus(existing.status ?? 'Open');
    setDueDate((existing.due_date ?? '').slice(0, 10));
    setDescription(existing.description ?? '');
  }, [existing]);

  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: p.id, label: p.project_name, description: p.name })),
    [projects],
  );

  function validate(): string[] {
    const out: string[] = [];
    if (!subject) out.push('Subject is required.');
    if (!projectID && (!parentDoctype || !parentID)) out.push('Set a project, or both a parent doctype and parent id.');
    if (parentDoctype && !parentID)  out.push('Parent ID is required when parent doctype is set.');
    if (!parentDoctype && parentID)  out.push('Pick a parent doctype to go with the parent ID.');
    return out;
  }

  function buildCreateBody() {
    return {
      subject,
      project_id:     projectID || undefined,
      parent_doctype: parentDoctype || undefined,
      parent_id:      parentID || undefined,
      assigned_to:    assignee || undefined,
      priority,
      due_date:       dueDate || undefined,
      description:    description || undefined,
    };
  }
  function buildUpdateBody() {
    return {
      subject,
      assigned_to: assignee || undefined,
      priority,
      due_date:    dueDate || undefined,
      description: description || undefined,
      status:      status || undefined,
    };
  }

  const createMutation = useMutation({
    mutationFn: () => api<Task>('/projects/tasks', { method: 'POST', body: buildCreateBody() }),
    onSuccess: (t) => {
      toast.success('Task created', t.subject);
      qc.invalidateQueries({ queryKey: ['doctype', '/projects/tasks'] });
      navigate({ to: `/projects/tasks/${t.id}` as never });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Create failed.'),
  });
  const updateMutation = useMutation({
    mutationFn: () => api<Task>(`/projects/tasks/${id}`, { method: 'PUT', body: buildUpdateBody() }),
    onSuccess: () => {
      toast.success('Saved');
      qc.invalidateQueries({ queryKey: ['task', id] });
      qc.invalidateQueries({ queryKey: ['doctype', '/projects/tasks'] });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Save failed.'),
  });
  const completeMutation = useMutation({
    mutationFn: () => api<Task>(`/projects/tasks/${id}/complete`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Completed');
      qc.invalidateQueries({ queryKey: ['task', id] });
      qc.invalidateQueries({ queryKey: ['doctype', '/projects/tasks'] });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Failed.'),
  });
  const reopenMutation = useMutation({
    mutationFn: () => api<Task>(`/projects/tasks/${id}/reopen`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Reopened');
      qc.invalidateQueries({ queryKey: ['task', id] });
      qc.invalidateQueries({ queryKey: ['doctype', '/projects/tasks'] });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Failed.'),
  });
  const deleteMutation = useMutation({
    mutationFn: () => api<{ status: string }>(`/projects/tasks/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.warning('Deleted', 'Task soft-deleted.');
      qc.invalidateQueries({ queryKey: ['doctype', '/projects/tasks'] });
      navigate({ to: '/projects/tasks' as never });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Delete failed.'),
  });

  function onCreate() {
    setErr(null);
    const issues = validate();
    if (issues.length > 0) { setErr(issues.join(' ')); toast.error('Fix these first', issues[0]); return; }
    createMutation.mutate();
  }
  function onSave() {
    setErr(null);
    const issues = validate();
    if (issues.length > 0) { setErr(issues.join(' ')); toast.error('Fix these first', issues[0]); return; }
    updateMutation.mutate();
  }

  const completed = status === 'Completed' || status === 'Cancelled';
  const title = isNew ? 'New task' : (existing?.subject ?? '…');
  const statusTone = status === 'Completed' ? 'success' : status === 'Cancelled' ? 'danger' : status === 'Working' ? 'accent' : 'neutral';

  return (
    <>
      <PageHeader
        crumbs={[
          { label: 'Operations', to: '/projects' },
          { label: 'Tasks', to: '/projects/tasks' },
          { label: isNew ? 'New' : title },
        ]}
        title={title}
        status={!isNew && <StatusPill tone={statusTone as never}>{status}</StatusPill>}
        actions={
          <>
            <Button variant="ghost" asChild>
              <Link to={'/projects/tasks' as never}><ArrowLeft className="size-4" /> Back</Link>
            </Button>
            {isNew && (
              <Button onClick={onCreate} loading={createMutation.isPending}>
                <Save className="size-4" /> Create <Kbd>⌘S</Kbd>
              </Button>
            )}
            {!isNew && (
              <Button variant="secondary" onClick={onSave} loading={updateMutation.isPending}>
                <Save className="size-4" /> Save
              </Button>
            )}
            {!isNew && !completed && (
              <Button onClick={() => completeMutation.mutate()} loading={completeMutation.isPending}>
                <CheckCircle2 className="size-4" /> Mark complete
              </Button>
            )}
            {!isNew && completed && (
              <Button variant="secondary" onClick={() => reopenMutation.mutate()} loading={reopenMutation.isPending}>
                <RotateCcw className="size-4" /> Reopen
              </Button>
            )}
            {!isNew && (
              <Button variant="danger" onClick={() => deleteMutation.mutate()} loading={deleteMutation.isPending}>
                <Trash2 className="size-4" /> Delete
              </Button>
            )}
          </>
        }
      />

      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-8 max-w-3xl space-y-4"
      >
        {err && (
          <Card className="!p-3 bg-brand-error/5 border-brand-error/30 flex items-start gap-2">
            <AlertCircle className="size-4 text-brand-error mt-0.5 shrink-0" />
            <div className="text-body-sm text-brand-error">{err}</div>
          </Card>
        )}

        <Card>
          <CardTitle>Details</CardTitle>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <Field label="Subject" htmlFor="f-subject" hint="Required.">
              <Input id="f-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Short task title" />
            </Field>
            <Field label="Status" htmlFor="f-status" hint={isNew ? 'Open by default.' : 'Use Complete / Reopen to flip terminal states.'}>
              <select id="f-status" className="input-base" value={status} onChange={(e) => setStatus(e.target.value as Status)} disabled={isNew}>
                <option value="Open">Open</option>
                <option value="Working">Working</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </Field>
            <Field label="Priority" htmlFor="f-priority">
              <select id="f-priority" className="input-base" value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Urgent">Urgent</option>
              </select>
            </Field>
            <Field label="Due date" htmlFor="f-due">
              <Input id="f-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </Field>
            <Field label="Assignee" hint="Free-text — user picker arrives with workflow.">
              <Input value={assignee} onChange={(e) => setAssignee(e.target.value)} placeholder="e.g. budi@example.com" />
            </Field>
          </div>
        </Card>

        <Card>
          <CardTitle>Attach to</CardTitle>
          <p className="text-caption text-stone mt-1">A task hangs off either a project, or another record (parent doctype + id). At least one is required.</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <Field label="Project">
              <Combobox
                options={projectOptions}
                value={projectID || null}
                onChange={(v) => setProjectID(v ?? '')}
                placeholder="— None —"
                clearable
                disabled={!isNew}
              />
            </Field>
            <Field label="Parent doctype">
              <select className="input-base" value={parentDoctype} onChange={(e) => setParentDoctype(e.target.value)} disabled={!isNew}>
                <option value="">— None —</option>
                {PARENT_DOCTYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </Field>
            {parentDoctype && (
              <Field label="Parent ID" hint="Paste the record ID — picker for cross-doctype links lands later.">
                <Input value={parentID} onChange={(e) => setParentID(e.target.value)} placeholder="e.g. cust_01..." className="font-mono text-caption" disabled={!isNew} />
              </Field>
            )}
          </div>
          {existing && (
            <p className="mt-3 text-caption text-stone">Attachment fields are locked after creation; create a new task to move it.</p>
          )}
        </Card>

        <Card>
          <CardTitle>Description</CardTitle>
          <Field label="">
            <textarea
              className="input-base min-h-[140px] py-2 mt-2"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Free text — context, links, acceptance criteria."
            />
          </Field>
        </Card>

        {existing && (
          <Card>
            <CardTitle>Meta</CardTitle>
            <div className="mt-3 grid grid-cols-2 gap-3 text-body-sm">
              <Row label="Code" value={existing.name} mono />
              {existing.due_date && <Row label="Due" value={date(existing.due_date)} />}
            </div>
          </Card>
        )}
      </motion.div>
    </>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-stone">{label}</span>
      <span className={mono ? 'font-mono text-caption text-charcoal' : 'text-ink'}>{value}</span>
    </div>
  );
}
